import React, { useState, useEffect, useCallback } from 'react';
import PortalLayout from './PortalLayout';
import { roleService } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import useIsMobile from '../../hooks/useIsMobile';
import MobileCard from '../../components/common/MobileCard';
import MobileHero from '../../components/common/MobileHero';
import { C } from '../../styles/theme';

// ─── Brand Tokens ───────────────────────────────────────────────────────────
const FONT = "'Plus Jakarta Sans', sans-serif";

const ROLE_COLORS = {
    Employee: { bg: '#eff6ff', text: '#2563eb', dot: '#2563eb' },
    Manager:  { bg: '#fffbeb', text: '#d97706', dot: '#d97706' },
    Admin:    { bg: '#eff5ff', text: '#2563eb', dot: '#2563eb' },
    _default: { bg: '#f5f3ff', text: '#7c3aed', dot: '#7c3aed' },
};

const getRoleColor = (name) => ROLE_COLORS[name] || ROLE_COLORS._default;

// ─── Helpers ────────────────────────────────────────────────────────────────
const AVATAR_PALETTE = ['#2563eb', '#2563eb', '#7c3aed', '#d97706', '#0891b2', '#dc2626', '#0d9488'];
const avatarColor = (name = '') => AVATAR_PALETTE[(name.charCodeAt(0) || 0) % AVATAR_PALETTE.length];
const initials = (name = '') => name.trim().split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();

const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
        const d = new Date(dateStr);
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch { return dateStr; }
};

const inputStyle = { width: '100%', border: `1px solid ${C.border}`, borderRadius: 8, padding: '9px 12px', fontSize: 13, color: C.text, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', transition: 'border-color 0.15s' };
const labelStyle = { display: 'block', fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 6 };

// ─── Sub-components ─────────────────────────────────────────────────────────

const Avatar = ({ name, size = 36 }) => (
    <div style={{ width: size, height: size, borderRadius: '50%', background: avatarColor(name), display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: size * 0.36, fontWeight: 700, flexShrink: 0 }}>
        {initials(name)}
    </div>
);

const StatusBadge = ({ active, t }) => (
    <span style={{ fontSize: 11, fontWeight: 700, borderRadius: 20, padding: '3px 10px', textTransform: 'uppercase', letterSpacing: 0.5, background: active ? C.greenLight : C.redLight, color: active ? C.green : C.red }}>
        {active ? t('active') : t('inactive')}
    </span>
);

const RoleBadge = ({ role }) => {
    const c = getRoleColor(role);
    return (
        <span style={{ fontSize: 11, fontWeight: 700, borderRadius: 20, padding: '3px 10px', textTransform: 'uppercase', letterSpacing: 0.5, background: c.bg, color: c.text }}>
            {role}
        </span>
    );
};

const Toggle = ({ on, onChange, disabled }) => (
    <div
        onClick={disabled ? undefined : onChange}
        style={{ width: 36, height: 20, borderRadius: 10, cursor: disabled ? 'default' : 'pointer', position: 'relative', transition: 'background 0.2s', background: on ? C.green : '#cbd5e1', opacity: disabled ? 0.5 : 1, flexShrink: 0 }}
    >
        <div style={{ position: 'absolute', top: 2, left: on ? 18 : 2, width: 16, height: 16, borderRadius: '50%', background: C.white, transition: 'left 0.2s', boxShadow: '0 1px 2px rgba(0,0,0,0.15)' }} />
    </div>
);

const Checkbox = ({ checked, onChange, disabled }) => (
    <div
        onClick={disabled ? undefined : onChange}
        style={{
            width: 18, height: 18, borderRadius: 4, cursor: disabled ? 'default' : 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s', flexShrink: 0,
            background: checked ? C.green : C.white,
            border: `2px solid ${checked ? C.green : '#cbd5e1'}`,
            opacity: disabled ? 0.5 : 1,
        }}
    >
        {checked && <span style={{ color: C.white, fontSize: 11, fontWeight: 800 }}>&#10003;</span>}
    </div>
);

const CloseButton = ({ onClick, size = 16 }) => (
    <button onClick={onClick} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.mutedLight, padding: 4, borderRadius: 4, display: 'flex' }}
        onMouseEnter={e => e.currentTarget.style.color = '#475569'} onMouseLeave={e => e.currentTarget.style.color = C.mutedLight}>
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
        </svg>
    </button>
);

const Toast = ({ message, type, onClose }) => {
    useEffect(() => { const t = setTimeout(onClose, 4000); return () => clearTimeout(t); }, [onClose]);
    return (
        <div style={{
            position: 'fixed', top: 20, right: 20, zIndex: 2000,
            background: type === 'error' ? '#fef2f2' : C.greenLight,
            border: `1px solid ${type === 'error' ? '#fecdd3' : C.greenBorder}`,
            color: type === 'error' ? C.red : C.greenDark,
            borderRadius: 10, padding: '12px 20px', fontSize: 13, fontWeight: 600,
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)', fontFamily: FONT,
            display: 'flex', alignItems: 'center', gap: 10,
        }}>
            {message}
            <CloseButton onClick={onClose} size={14} />
        </div>
    );
};

// ─── Empty States ───────────────────────────────────────────────────────────

const RolesEmptyState = ({ filtered, t }) => (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '48px 24px', color: C.mutedLight }}>
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: 12, opacity: 0.4 }}>
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
        </svg>
        <p style={{ margin: '0 0 4px', fontSize: 14, fontWeight: 600, color: '#475569' }}>
            {filtered ? t('no_roles_match_search') : t('no_roles_yet')}
        </p>
        <p style={{ margin: 0, fontSize: 12, textAlign: 'center' }}>
            {filtered ? t('try_different_search') : t('roles_will_appear_once_created')}
        </p>
    </div>
);

const UsersEmptyState = ({ filtered, onAdd, t }) => (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '64px 24px', color: C.mutedLight }}>
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: 16, opacity: 0.4 }}>
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
        </svg>
        <p style={{ margin: '0 0 4px', fontSize: 15, fontWeight: 600, color: '#475569' }}>
            {filtered ? t('no_users_match_filters') : t('no_users_yet')}
        </p>
        <p style={{ margin: '0 0 20px', fontSize: 13 }}>
            {filtered ? t('try_adjusting_search_or_filter') : t('add_first_user')}
        </p>
        {!filtered && (
            <button onClick={onAdd} style={{ background: C.green, color: C.white, border: 'none', borderRadius: 8, padding: '9px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                {t('add_user')}
            </button>
        )}
    </div>
);

// ─── Permission Editor ──────────────────────────────────────────────────────

const PermissionEditor = ({ role, permissions, onSave, saving, t }) => {
    const [local, setLocal] = useState({});
    const [dirty, setDirty] = useState(false);

    // Reset local state when role/permissions change
    useEffect(() => {
        if (permissions) {
            const map = {};
            permissions.forEach(p => { map[p.permission_key] = { can_view: p.can_view, can_edit: p.can_edit }; });
            setLocal(map);
            setDirty(false);
        }
    }, [permissions]);

    const toggle = (key, field) => {
        setLocal(prev => {
            const cur = prev[key] || { can_view: false, can_edit: false };
            const next = { ...cur };
            if (field === 'can_view') {
                next.can_view = !cur.can_view;
                // Unchecking view also unchecks edit
                if (!next.can_view) next.can_edit = false;
            } else if (field === 'can_edit') {
                next.can_edit = !cur.can_edit;
                // Checking edit auto-enables view
                if (next.can_edit) next.can_view = true;
            } else {
                // POS toggle: just flip can_view
                next.can_view = !cur.can_view;
            }
            return { ...prev, [key]: next };
        });
        setDirty(true);
    };

    const handleReset = () => {
        if (permissions) {
            const map = {};
            permissions.forEach(p => { map[p.permission_key] = { can_view: p.can_view, can_edit: p.can_edit }; });
            setLocal(map);
            setDirty(false);
        }
    };

    const handleSave = () => {
        const arr = Object.entries(local).map(([key, val]) => ({
            permission_key: key,
            can_view: val.can_view,
            can_edit: val.can_edit,
        }));
        onSave(arr);
    };

    if (!role) {
        return (
            <div style={{ flex: 1, background: C.white, border: `1px solid ${C.border}`, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.mutedLight }}>
                <div style={{ textAlign: 'center' }}>
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: 12, opacity: 0.3 }}>
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                    </svg>
                    <p style={{ fontSize: 14, fontWeight: 600, color: '#475569', margin: '0 0 4px' }}>{t('select_a_role')}</p>
                    <p style={{ fontSize: 12, margin: 0 }}>{t('choose_role_to_edit_permissions')}</p>
                </div>
            </div>
        );
    }

    // Group permissions by category
    const posPerms = (permissions || []).filter(p => p.category === 'POS');
    const portalPerms = (permissions || []).filter(p => p.category === 'Portal');
    const rc = getRoleColor(role.name);

    return (
        <div style={{ flex: 1, background: C.white, border: `1px solid ${C.border}`, borderRadius: 12, overflowY: 'auto', padding: 24 }}>
            {/* Header */}
            <div style={{ marginBottom: 24 }}>
                <div style={{ fontSize: 18, fontWeight: 800, color: C.text, marginBottom: 4 }}>{role.name}</div>
                <div style={{ fontSize: 13, color: C.muted }}>{role.description}</div>
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                    {role.is_default && (
                        <span style={{ fontSize: 11, fontWeight: 700, borderRadius: 20, padding: '3px 10px', background: rc.bg, color: rc.text, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            {t('default_role')}
                        </span>
                    )}
                    <span style={{ fontSize: 11, fontWeight: 600, borderRadius: 20, padding: '3px 10px', background: '#f1f5f9', color: C.muted }}>
                        {role.user_count} {role.user_count !== 1 ? t('users') : t('user')} {t('assigned')}
                    </span>
                </div>
            </div>

            {/* POS Section */}
            {posPerms.length > 0 && (
                <div style={{ marginBottom: 24 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: C.muted, marginBottom: 12, paddingBottom: 8, borderBottom: `1px solid ${C.border}` }}>
                        POS
                    </div>
                    {posPerms.map(p => (
                        <div key={p.permission_key} style={{ display: 'flex', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #f1f5f9' }}>
                            <span style={{ flex: 1, fontSize: 13, fontWeight: 500, color: C.text }}>{p.label}</span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span style={{ fontSize: 11, color: C.muted, fontWeight: 600 }}>{t('access')}</span>
                                <Toggle on={local[p.permission_key]?.can_view || false} onChange={() => toggle(p.permission_key, 'toggle')} />
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Portal Section */}
            {portalPerms.length > 0 && (
                <div style={{ marginBottom: 24 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: C.muted, marginBottom: 12, paddingBottom: 8, borderBottom: `1px solid ${C.border}` }}>
                        Portal
                    </div>
                    {portalPerms.map(p => (
                        <div key={p.permission_key} style={{ display: 'flex', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #f1f5f9' }}>
                            <span style={{ flex: 1, fontSize: 13, fontWeight: 500, color: C.text }}>{p.label}</span>
                            <div style={{ display: 'flex', gap: 24 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <span style={{ fontSize: 11, color: C.muted, fontWeight: 600 }}>{t('view')}</span>
                                    <Checkbox checked={local[p.permission_key]?.can_view || false} onChange={() => toggle(p.permission_key, 'can_view')} />
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <span style={{ fontSize: 11, color: C.muted, fontWeight: 600 }}>{t('edit')}</span>
                                    <Checkbox checked={local[p.permission_key]?.can_edit || false} onChange={() => toggle(p.permission_key, 'can_edit')} />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Save bar */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, paddingTop: 20, marginTop: 8, borderTop: `1px solid ${C.border}` }}>
                <button onClick={handleReset} disabled={!dirty} style={{
                    background: C.white, color: C.text, border: `1px solid ${C.border}`, borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: dirty ? 'pointer' : 'default', opacity: dirty ? 1 : 0.5, fontFamily: 'inherit',
                }}>
                    {t('reset')}
                </button>
                <button onClick={handleSave} disabled={!dirty || saving} style={{
                    background: C.green, color: C.white, border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600,
                    cursor: (!dirty || saving) ? 'default' : 'pointer', opacity: (!dirty || saving) ? 0.6 : 1, fontFamily: 'inherit', transition: 'background 0.15s',
                }}
                    onMouseEnter={e => { if (dirty && !saving) e.currentTarget.style.background = C.greenDark; }}
                    onMouseLeave={e => { if (dirty && !saving) e.currentTarget.style.background = C.green; }}
                >
                    {saving ? t('saving') : t('save_changes')}
                </button>
            </div>
        </div>
    );
};

// ─── Role Form Modal (Add/Edit) ─────────────────────────────────────────────

const RoleFormModal = ({ role, onSave, onClose, saving, t }) => {
    const isEdit = !!role;
    const [name, setName] = useState(role?.name || '');
    const [description, setDescription] = useState(role?.description || '');
    const [error, setError] = useState('');
    const nameDisabled = isEdit && role?.is_default;

    const handleSubmit = () => {
        if (!nameDisabled && !name.trim()) return setError(t('role_name_required'));
        setError('');
        onSave({ name: nameDisabled ? role.name : name.trim(), description: description.trim() });
    };

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
            onClick={e => e.target === e.currentTarget && onClose()}>
            <div className="portal-modal-card" style={{ background: C.white, borderRadius: 16, width: 480, maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}
                onKeyDown={e => e.key === 'Escape' && onClose()}>
                {/* Header */}
                <div style={{ padding: '20px 24px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 18, fontWeight: 800, color: C.text }}>{isEdit ? t('edit_role') : t('add_new_role')}</span>
                    <CloseButton onClick={onClose} size={18} />
                </div>
                {/* Body */}
                <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 18 }}>
                    {error && <div style={{ background: '#fff1f2', border: '1px solid #fecdd3', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#e11d48' }}>{error}</div>}
                    <div>
                        <label style={labelStyle}>{t('role_name')} {!nameDisabled && <span style={{ color: C.red }}>*</span>}</label>
                        <input
                            style={{ ...inputStyle, ...(nameDisabled ? { background: '#f1f5f9', cursor: 'not-allowed' } : {}) }}
                            value={name} onChange={e => setName(e.target.value)}
                            placeholder={t('role_name_placeholder')}
                            disabled={nameDisabled}
                        />
                        {nameDisabled && <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>{t('default_role_names_cannot_change')}</div>}
                    </div>
                    <div>
                        <label style={labelStyle}>{t('description')}</label>
                        <input style={inputStyle} value={description} onChange={e => setDescription(e.target.value)} placeholder={t('role_description_placeholder')} />
                    </div>
                </div>
                {/* Footer */}
                <div style={{ padding: '16px 24px', borderTop: `1px solid ${C.border}`, display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                    <button onClick={onClose} style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, color: '#475569', cursor: 'pointer', fontFamily: 'inherit' }}>
                        {t('cancel')}
                    </button>
                    <button onClick={handleSubmit} disabled={saving} style={{
                        background: C.green, color: C.white, border: 'none', borderRadius: 8, padding: '8px 20px', fontSize: 13, fontWeight: 600,
                        cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.7 : 1, fontFamily: 'inherit', transition: 'background 0.15s',
                    }}
                        onMouseEnter={e => { if (!saving) e.currentTarget.style.background = C.greenDark; }}
                        onMouseLeave={e => { if (!saving) e.currentTarget.style.background = C.green; }}
                    >
                        {saving ? t('saving') : isEdit ? t('save_changes') : t('create_role')}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ─── User Form Modal (Add/Edit) ─────────────────────────────────────────────

const UserFormModal = ({ user, roles, allPermissions, onSave, onClose, saving, t }) => {
    const isEdit = !!user;
    const [form, setForm] = useState({
        display_name: user?.display_name || user?.username || '',
        username: user?.username || '',
        role: user?._roleName || (roles[0]?.name || ''),
        role_id: user?.role_id || (roles[0]?.id || null),
        password: '',
    });
    const [showPw, setShowPw] = useState(false);
    const [error, setError] = useState('');

    const set = (field, value) => setForm(f => ({ ...f, [field]: value }));

    const selectedRole = roles.find(r => r.name === form.role);

    const handleRoleChange = (roleName) => {
        const r = roles.find(x => x.name === roleName);
        setForm(f => ({ ...f, role: roleName, role_id: r?.id || null }));
    };

    const handleSubmit = () => {
        if (!form.display_name.trim()) return setError(t('display_name_required'));
        if (!form.username.trim()) return setError(t('username_required'));
        if (!isEdit && !form.password) return setError(t('password_required'));
        if (!isEdit && form.password.length < 4) return setError(t('password_min_4_chars'));
        setError('');
        onSave({
            ...form,
            display_name: form.display_name.trim(),
            username: form.username.trim(),
        });
    };

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
            onClick={e => e.target === e.currentTarget && onClose()}>
            <div className="portal-modal-card" style={{ background: C.white, borderRadius: 16, width: 520, maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}
                onKeyDown={e => e.key === 'Escape' && onClose()}>
                {/* Header */}
                <div style={{ padding: '20px 24px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 18, fontWeight: 800, color: C.text }}>{isEdit ? t('edit_user') : t('add_new_user')}</span>
                    <CloseButton onClick={onClose} size={18} />
                </div>
                {/* Body */}
                <div style={{ flex: 1, overflowY: 'auto', padding: 24, display: 'flex', flexDirection: 'column', gap: 18 }}>
                    {error && <div style={{ background: '#fff1f2', border: '1px solid #fecdd3', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#e11d48' }}>{error}</div>}

                    <div>
                        <label style={labelStyle}>{t('display_name')} <span style={{ color: C.red }}>*</span></label>
                        <input style={inputStyle} value={form.display_name} onChange={e => set('display_name', e.target.value)} placeholder={t('display_name_placeholder')} />
                    </div>

                    <div className="portal-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                        <div>
                            <label style={labelStyle}>{t('username')} <span style={{ color: C.red }}>*</span></label>
                            <input style={{ ...inputStyle, ...(isEdit ? { background: '#f1f5f9', cursor: 'not-allowed' } : {}) }} value={form.username} onChange={e => set('username', e.target.value)} placeholder={t('username_placeholder')} disabled={isEdit} />
                        </div>
                        <div>
                            <label style={labelStyle}>{t('role')} <span style={{ color: C.red }}>*</span></label>
                            <select style={{ ...inputStyle, cursor: 'pointer', background: C.white }} value={form.role} onChange={e => handleRoleChange(e.target.value)}>
                                {roles.map(r => <option key={r.id} value={r.name}>{r.name}</option>)}
                            </select>
                        </div>
                    </div>

                    {!isEdit && (
                        <div>
                            <label style={labelStyle}>{t('temporary_password')} <span style={{ color: C.red }}>*</span></label>
                            <div style={{ position: 'relative' }}>
                                <input style={{ ...inputStyle, paddingRight: 50 }} type={showPw ? 'text' : 'password'} value={form.password} onChange={e => set('password', e.target.value)} placeholder={t('min_4_characters')} />
                                <button type="button" onClick={() => setShowPw(v => !v)} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: C.muted, fontSize: 12, fontWeight: 600, fontFamily: 'inherit' }}>
                                    {showPw ? t('hide') : t('show')}
                                </button>
                            </div>
                            <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>{t('user_prompted_change_password')}</div>
                        </div>
                    )}

                    {/* The POS PIN field that used to live here was REMOVED
                        (2026-08-24). It was dead: handleSaveUser only ever sent
                        role_id and display_name, so `form.pin` was collected,
                        validated and silently discarded — while the UI showed a
                        green "POS access - set PIN" confirmation for a value
                        that never left the browser. A real tenant lost time to
                        it, setting a manager's PIN here and finding the POS
                        rejected it.

                        PINs belong to the EMPLOYEE record, not the portal user
                        credential: /auth/employee-login resolves a PIN against
                        employee_access joined to an ACTIVE employee row, and a
                        user here may have no employee row at all. Set PINs in
                        Employees, which posts to /employees/v2 and writes both
                        stores. */}
                </div>
                {/* Footer */}
                <div style={{ padding: '16px 24px', borderTop: `1px solid ${C.border}`, display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                    <button onClick={onClose} style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, color: '#475569', cursor: 'pointer', fontFamily: 'inherit' }}>
                        {t('cancel')}
                    </button>
                    <button onClick={handleSubmit} disabled={saving} style={{
                        background: C.green, color: C.white, border: 'none', borderRadius: 8, padding: '8px 20px', fontSize: 13, fontWeight: 600,
                        cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.7 : 1, fontFamily: 'inherit', transition: 'background 0.15s',
                    }}
                        onMouseEnter={e => { if (!saving) e.currentTarget.style.background = C.greenDark; }}
                        onMouseLeave={e => { if (!saving) e.currentTarget.style.background = C.green; }}
                    >
                        {saving ? t('saving') : isEdit ? t('save_changes') : t('create_user')}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ─── Reset Password Modal ───────────────────────────────────────────────────

const ResetPasswordModal = ({ user, onConfirm, onClose, saving, t }) => {
    const [pw, setPw] = useState('');
    const [pw2, setPw2] = useState('');
    const [showPw, setShowPw] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = () => {
        if (!pw) return setError(t('password_required'));
        if (pw.length < 4) return setError(t('password_min_4_chars'));
        if (pw !== pw2) return setError(t('passwords_do_not_match'));
        setError('');
        onConfirm(pw);
    };

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
            onClick={e => e.target === e.currentTarget && onClose()}>
            <div className="portal-modal-card" style={{ background: C.white, borderRadius: 16, width: 420, padding: 0, boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}
                onKeyDown={e => e.key === 'Escape' && onClose()}>
                <div style={{ padding: '20px 24px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 16, fontWeight: 700, color: C.text }}>{t('reset_password')}</span>
                    <CloseButton onClick={onClose} size={18} />
                </div>
                <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <div style={{ fontSize: 13, color: C.muted }}>
                        {t('set_new_password_for')} <strong>{user?.display_name || user?.username}</strong>.
                    </div>
                    {error && <div style={{ background: '#fff1f2', border: '1px solid #fecdd3', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#e11d48' }}>{error}</div>}
                    <div>
                        <label style={labelStyle}>{t('new_password')}</label>
                        <div style={{ position: 'relative' }}>
                            <input style={{ ...inputStyle, paddingRight: 50 }} type={showPw ? 'text' : 'password'} value={pw} onChange={e => setPw(e.target.value)} placeholder={t('min_4_characters')} />
                            <button type="button" onClick={() => setShowPw(v => !v)} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: C.muted, fontSize: 12, fontWeight: 600, fontFamily: 'inherit' }}>
                                {showPw ? t('hide') : t('show')}
                            </button>
                        </div>
                    </div>
                    <div>
                        <label style={labelStyle}>{t('confirm_password')}</label>
                        <input style={inputStyle} type={showPw ? 'text' : 'password'} value={pw2} onChange={e => setPw2(e.target.value)} placeholder={t('repeat_password')} />
                    </div>
                </div>
                <div style={{ padding: '16px 24px', borderTop: `1px solid ${C.border}`, display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                    <button onClick={onClose} style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, color: '#475569', cursor: 'pointer', fontFamily: 'inherit' }}>{t('cancel')}</button>
                    <button onClick={handleSubmit} disabled={saving} style={{
                        background: C.green, color: C.white, border: 'none', borderRadius: 8, padding: '8px 20px', fontSize: 13, fontWeight: 600,
                        cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.7 : 1, fontFamily: 'inherit',
                    }}>
                        {saving ? t('resetting') : t('reset_password')}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ─── Confirm Deactivate Modal ───────────────────────────────────────────────

const DeactivateModal = ({ user, onConfirm, onClose, saving, t }) => (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
        onClick={e => e.target === e.currentTarget && onClose()}>
        <div className="portal-modal-card" style={{ background: C.white, borderRadius: 16, width: 420, padding: '28px 28px 24px', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}
            onKeyDown={e => e.key === 'Escape' && onClose()}>
            <div style={{ fontSize: 16, fontWeight: 700, color: C.text, marginBottom: 8 }}>
                {user?.is_active ? t('deactivate_account') : t('reactivate_account')}
            </div>
            <div style={{ fontSize: 13, color: C.muted, marginBottom: 24 }}>
                {user?.is_active
                    ? <>{t('confirm_deactivate_user')} <strong>{user?.display_name || user?.username}</strong>? {t('they_will_lose_access')}</>
                    : <>{t('reactivate')} <strong>{user?.display_name || user?.username}</strong>? {t('they_will_regain_access')}</>
                }
            </div>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                <button onClick={onClose} style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, color: '#475569', cursor: 'pointer', fontFamily: 'inherit' }}>{t('cancel')}</button>
                <button onClick={onConfirm} disabled={saving} style={{
                    background: user?.is_active ? '#e11d48' : C.green, color: C.white, border: 'none', borderRadius: 8, padding: '8px 18px', fontSize: 13, fontWeight: 600,
                    cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.7 : 1, fontFamily: 'inherit',
                }}>
                    {saving ? t('processing') : user?.is_active ? t('deactivate') : t('reactivate')}
                </button>
            </div>
        </div>
    </div>
);

// ─── Delete Role Modal ──────────────────────────────────────────────────────

const DeleteRoleModal = ({ role, onConfirm, onClose, saving, t }) => (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
        onClick={e => e.target === e.currentTarget && onClose()}>
        <div className="portal-modal-card" style={{ background: C.white, borderRadius: 16, width: 420, padding: '28px 28px 24px', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}
            onKeyDown={e => e.key === 'Escape' && onClose()}>
            <div style={{ fontSize: 16, fontWeight: 700, color: C.text, marginBottom: 8 }}>{t('delete_role')}</div>
            <div style={{ fontSize: 13, color: C.muted, marginBottom: 24 }}>
                {t('confirm_delete_role')} <strong>{role?.name}</strong>? {t('this_cannot_be_undone')}
            </div>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                <button onClick={onClose} style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, color: '#475569', cursor: 'pointer', fontFamily: 'inherit' }}>{t('cancel')}</button>
                <button onClick={onConfirm} disabled={saving} style={{
                    background: '#e11d48', color: C.white, border: 'none', borderRadius: 8, padding: '8px 18px', fontSize: 13, fontWeight: 600,
                    cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.7 : 1, fontFamily: 'inherit',
                }}>
                    {saving ? t('deleting') : t('delete')}
                </button>
            </div>
        </div>
    </div>
);

// ─── User Detail Panel ──────────────────────────────────────────────────────

const UserDetailPanel = ({ user, onClose, onEdit, onResetPw, onToggleActive, roleName, t }) => {
    if (!user) return null;
    const fields = [
        { label: t('role'), value: <RoleBadge role={roleName || user.role} /> },
        { label: t('status'), value: <StatusBadge active={user.is_active} t={t} /> },
        { label: t('short_id'), value: user.short_id || '—' },
        { label: t('created'), value: formatDate(user.created_at) },
        { label: t('last_updated'), value: formatDate(user.updated_at) },
    ];

    return (
        <div style={{ width: 320, flexShrink: 0, background: C.white, borderLeft: `1px solid ${C.border}`, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Close */}
            <div style={{ padding: '16px 20px', display: 'flex', justifyContent: 'flex-end' }}>
                <CloseButton onClick={onClose} />
            </div>
            {/* Avatar + Name */}
            <div style={{ padding: '0 24px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                <Avatar name={user.display_name || user.username} size={64} />
                <div style={{ fontSize: 16, fontWeight: 700, color: C.text, marginTop: 12 }}>{user.display_name || user.username}</div>
                <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{user.username}</div>
            </div>
            {/* Fields */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
                {fields.map(f => (
                    <div key={f.label}>
                        <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: C.muted, marginBottom: 4 }}>{f.label}</div>
                        <div style={{ fontSize: 13, color: C.text, fontWeight: 500 }}>{f.value}</div>
                    </div>
                ))}
            </div>
            {/* Actions */}
            <div style={{ padding: '16px 24px', borderTop: `1px solid ${C.border}`, display: 'flex', flexDirection: 'column', gap: 8 }}>
                <button onClick={onEdit} style={{
                    width: '100%', background: C.white, border: `1px solid ${C.border}`, borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, color: C.text, cursor: 'pointer', fontFamily: 'inherit',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, transition: 'background 0.15s',
                }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f1f5f9'} onMouseLeave={e => e.currentTarget.style.background = C.white}>
                    {t('edit_user')}
                </button>
                <button onClick={onResetPw} style={{
                    width: '100%', background: C.white, border: `1px solid ${C.border}`, borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, color: C.text, cursor: 'pointer', fontFamily: 'inherit',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, transition: 'background 0.15s',
                }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f1f5f9'} onMouseLeave={e => e.currentTarget.style.background = C.white}>
                    {t('reset_password')}
                </button>
                <button onClick={onToggleActive} style={{
                    width: '100%', background: user.is_active ? C.redLight : C.greenLight, border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600,
                    color: user.is_active ? C.red : C.green, cursor: 'pointer', fontFamily: 'inherit',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, transition: 'background 0.15s',
                }}>
                    {user.is_active ? t('deactivate_account') : t('reactivate_account')}
                </button>
            </div>
        </div>
    );
};

// ─── Mobile User Card ─────────────────────────────────────────────────────────

const MobileUserCard = ({ user, roleName, permissions, onEdit, onResetPw, onToggleActive, t }) => {
    const displayName = user.display_name || user.username;
    const viewable = (permissions || []).filter(p => p.can_view);
    const editable = (permissions || []).filter(p => p.can_edit);
    return (
        <MobileCard
            accent={user.is_active ? undefined : 'red'}
            avatar={{ initials: initials(displayName), ping: user.is_active ? 'green' : 'red' }}
            title={displayName}
            status={{ label: roleName, tone: 'gray' }}
            subtitle={user.username}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 13 }}>
                    <span style={{ color: C.muted, fontWeight: 600 }}>{t('status')}</span>
                    <StatusBadge active={user.is_active} t={t} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 13 }}>
                    <span style={{ color: C.muted, fontWeight: 600 }}>{t('created')}</span>
                    <span style={{ color: C.text, fontWeight: 600 }}>{formatDate(user.created_at)}</span>
                </div>
                {permissions && (
                    <div style={{ fontSize: 12, color: C.muted }}>
                        <span style={{ fontWeight: 700, color: C.text }}>{viewable.length}</span> {t('viewable') || 'viewable'} ·{' '}
                        <span style={{ fontWeight: 700, color: C.text }}>{editable.length}</span> {t('editable') || 'editable'}
                    </div>
                )}
                <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                    <button onClick={() => onEdit(user)} style={{
                        flex: 1, padding: '11px 12px', borderRadius: 8, border: `1px solid ${C.border}`,
                        background: C.white, color: C.text, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                    }}>{t('edit')}</button>
                    <button onClick={() => onResetPw(user)} style={{
                        flex: 1, padding: '11px 12px', borderRadius: 8, border: `1px solid ${C.border}`,
                        background: C.white, color: C.text, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                    }}>{t('reset_pw')}</button>
                    <button onClick={() => onToggleActive(user)} style={{
                        flex: 1, padding: '11px 12px', borderRadius: 8, border: 'none',
                        background: user.is_active ? C.redLight : C.greenLight,
                        color: user.is_active ? C.red : C.green, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                    }}>{user.is_active ? t('deactivate') : t('reactivate')}</button>
                </div>
            </div>
        </MobileCard>
    );
};

// ─── Main Page ──────────────────────────────────────────────────────────────

const PortalRolesPage = () => {
    const { tenantId, username } = useAuth();
    const { t } = useLanguage();
    const isMobile = useIsMobile();

    // Tab
    const [activeTab, setActiveTab] = useState('roles');

    // Data
    const [roles, setRoles] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [allPermissions, setAllPermissions] = useState({}); // roleId -> permissions[]

    // Roles tab state
    const [selectedRole, setSelectedRole] = useState(null);
    const [rolePerms, setRolePerms] = useState(null);
    const [roleSearch, setRoleSearch] = useState('');
    const [showRoleForm, setShowRoleForm] = useState(false);
    const [editRoleTarget, setEditRoleTarget] = useState(null);
    const [deleteRoleTarget, setDeleteRoleTarget] = useState(null);

    // Users tab state
    const [userSearch, setUserSearch] = useState('');
    const [filterRole, setFilterRole] = useState('all');
    const [filterStatus, setFilterStatus] = useState('all');
    const [selectedUser, setSelectedUser] = useState(null);
    const [showUserForm, setShowUserForm] = useState(false);
    const [editUserTarget, setEditUserTarget] = useState(null);
    const [resetPwTarget, setResetPwTarget] = useState(null);
    const [deactivateTarget, setDeactivateTarget] = useState(null);

    // Shared
    const [saving, setSaving] = useState(false);
    const [toast, setToast] = useState(null);

    // Resolve display role name from role_id
    const getUserRoleName = useCallback((user) => {
        if (user.role_id) {
            const r = roles.find(x => x.id === user.role_id);
            if (r) return r.name;
        }
        return t('unassigned');
    }, [roles]);

    const showToast = useCallback((message, type = 'success') => setToast({ message, type }), []);

    // ─── Data Loading ───────────────────────────────────────────────────────
    const loadRoles = useCallback(async () => {
        try {
            const data = await roleService.getRoles(tenantId);
            setRoles(data);
            return data;
        } catch { showToast(t('failed_to_load_roles'), 'error'); return []; }
    }, [tenantId, showToast]);

    const loadUsers = useCallback(async () => {
        try {
            const data = await roleService.getCredentials(tenantId);
            setUsers(data);
        } catch { showToast(t('failed_to_load_users'), 'error'); }
    }, [tenantId, showToast]);

    const loadAllPermissions = useCallback(async (rolesList) => {
        const map = {};
        await Promise.all(
            rolesList.map(async (r) => {
                try {
                    const perms = await roleService.getRolePermissions(r.id, tenantId);
                    map[r.id] = perms;
                } catch { /* ignore */ }
            })
        );
        setAllPermissions(map);
    }, [tenantId]);

    const loadAll = useCallback(async () => {
        setLoading(true);
        const rolesList = await loadRoles();
        await Promise.all([loadUsers(), loadAllPermissions(rolesList)]);
        setLoading(false);
    }, [loadRoles, loadUsers, loadAllPermissions]);

    useEffect(() => { if (tenantId) loadAll(); }, [tenantId, loadAll]);

    // Load permissions for selected role
    const loadRolePerms = useCallback(async (roleId) => {
        try {
            const perms = await roleService.getRolePermissions(roleId, tenantId);
            setRolePerms(perms);
        } catch { showToast(t('failed_to_load_permissions'), 'error'); }
    }, [tenantId, showToast]);

    useEffect(() => {
        if (selectedRole) loadRolePerms(selectedRole.id);
        else setRolePerms(null);
    }, [selectedRole, loadRolePerms]);

    // ─── Role Handlers ──────────────────────────────────────────────────────
    const handleSaveRole = async (data) => {
        setSaving(true);
        try {
            if (editRoleTarget) {
                await roleService.updateRole(editRoleTarget.id, tenantId, data);
                showToast(t('role_updated'));
            } else {
                await roleService.createRole(tenantId, data);
                showToast(t('role_created'));
            }
            setShowRoleForm(false);
            setEditRoleTarget(null);
            const newRoles = await loadRoles();
            await loadAllPermissions(newRoles);
        } catch (err) {
            showToast(err?.response?.data?.detail || t('failed_to_save_role'), 'error');
        } finally { setSaving(false); }
    };

    const handleDeleteRole = async () => {
        if (!deleteRoleTarget) return;
        if (deleteRoleTarget.user_count > 0) {
            showToast(t('cannot_delete_role_has_users', { name: deleteRoleTarget.name, count: deleteRoleTarget.user_count }), 'error');
            setDeleteRoleTarget(null);
            return;
        }
        setSaving(true);
        try {
            await roleService.deleteRole(deleteRoleTarget.id, tenantId);
            showToast(t('role_deleted'));
            setDeleteRoleTarget(null);
            if (selectedRole?.id === deleteRoleTarget.id) { setSelectedRole(null); setRolePerms(null); }
            await loadRoles();
        } catch (err) {
            showToast(err?.response?.data?.detail || t('failed_to_delete_role'), 'error');
        } finally { setSaving(false); }
    };

    const handleSavePermissions = async (permArr) => {
        if (!selectedRole) return;
        setSaving(true);
        try {
            await roleService.updateRolePermissions(selectedRole.id, tenantId, permArr);
            showToast(t('permissions_saved'));
            await loadRolePerms(selectedRole.id);
            // Refresh allPermissions for the saved role
            const perms = await roleService.getRolePermissions(selectedRole.id, tenantId);
            setAllPermissions(prev => ({ ...prev, [selectedRole.id]: perms }));
        } catch (err) {
            showToast(err?.response?.data?.detail || t('failed_to_save_permissions'), 'error');
        } finally { setSaving(false); }
    };

    // ─── User Handlers ──────────────────────────────────────────────────────
    const handleSaveUser = async (form) => {
        setSaving(true);
        try {
            if (editUserTarget) {
                await roleService.updateCredential(editUserTarget.id, tenantId, {
                    role_id: form.role_id,
                    display_name: form.display_name.trim(),
                });
                showToast(t('user_updated'));
            } else {
                // Derive short_id from existing credentials (all share the tenant's short_id)
                const tenantShortId = users.find(u => u.short_id)?.short_id || null;
                await roleService.createCredential(tenantId, {
                    username: form.username,
                    password: form.password,
                    entity_id: tenantId,
                    short_id: tenantShortId,
                    role_id: form.role_id,
                    display_name: form.display_name.trim(),
                });
                showToast(t('user_created'));
            }
            setShowUserForm(false);
            setEditUserTarget(null);
            setSelectedUser(null);
            await loadUsers();
            await loadRoles(); // refresh user counts
        } catch (err) {
            showToast(err?.response?.data?.detail || t('failed_to_save_user'), 'error');
        } finally { setSaving(false); }
    };

    const handleResetPassword = async (newPw) => {
        if (!resetPwTarget) return;
        setSaving(true);
        try {
            await roleService.resetPassword(resetPwTarget.id, tenantId, newPw);
            showToast(t('password_reset_successfully'));
            setResetPwTarget(null);
        } catch (err) {
            showToast(err?.response?.data?.detail || t('failed_to_reset_password'), 'error');
        } finally { setSaving(false); }
    };

    const handleToggleActive = async () => {
        if (!deactivateTarget) return;
        // Self-protection
        if (deactivateTarget.is_active && deactivateTarget.username === username) {
            showToast(t('cannot_deactivate_own_account'), 'error');
            setDeactivateTarget(null);
            return;
        }
        setSaving(true);
        try {
            await roleService.updateCredential(deactivateTarget.id, tenantId, { is_active: !deactivateTarget.is_active });
            showToast(deactivateTarget.is_active ? t('account_deactivated') : t('account_reactivated'));
            setDeactivateTarget(null);
            if (selectedUser?.id === deactivateTarget.id) setSelectedUser(null);
            await loadUsers();
            await loadRoles();
        } catch (err) {
            showToast(err?.response?.data?.detail || t('failed_to_update_account'), 'error');
        } finally { setSaving(false); }
    };

    // ─── Filtered Data ──────────────────────────────────────────────────────
    const filteredRoles = roles.filter(r => {
        if (roleSearch && !r.name.toLowerCase().includes(roleSearch.toLowerCase()) && !(r.description || '').toLowerCase().includes(roleSearch.toLowerCase())) return false;
        return true;
    });

    const filteredUsers = users.filter(u => {
        if (userSearch) {
            const q = userSearch.toLowerCase();
            if (!(u.display_name || '').toLowerCase().includes(q) && !u.username.toLowerCase().includes(q)) return false;
        }
        if (filterRole !== 'all' && getUserRoleName(u).toLowerCase() !== filterRole.toLowerCase()) return false;
        if (filterStatus !== 'all') {
            const isActive = u.is_active;
            if (filterStatus === 'active' && !isActive) return false;
            if (filterStatus === 'inactive' && isActive) return false;
        }
        return true;
    });

    // ─── Tab styles ─────────────────────────────────────────────────────────
    const tabBtnStyle = (active) => ({
        padding: '12px 22px', cursor: 'pointer', fontSize: 14, fontWeight: 700,
        color: active ? C.green : C.muted, border: 'none', background: 'none',
        borderBottom: `2px solid ${active ? C.green : 'transparent'}`,
        marginBottom: -1,
        fontFamily: FONT, transition: 'all 0.2s',
    });

    // ─── Render ─────────────────────────────────────────────────────────────
    return (
        <PortalLayout>
            <div style={{ fontFamily: FONT, display: 'flex', flexDirection: 'column', height: '100%' }}>
                {/* Toast */}
                {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

                {/* Tab switcher bar */}
                <div style={{ background: C.white, display: 'flex', gap: 4, padding: isMobile ? '0 16px' : '0 24px', borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
                    <button style={tabBtnStyle(activeTab === 'roles')} onClick={() => setActiveTab('roles')}
                        onMouseEnter={e => { if (activeTab !== 'roles') e.currentTarget.style.color = C.text; }}
                        onMouseLeave={e => { if (activeTab !== 'roles') e.currentTarget.style.color = C.muted; }}
                    >{t('roles')}</button>
                    <button style={tabBtnStyle(activeTab === 'users')} onClick={() => setActiveTab('users')}
                        onMouseEnter={e => { if (activeTab !== 'users') e.currentTarget.style.color = C.text; }}
                        onMouseLeave={e => { if (activeTab !== 'users') e.currentTarget.style.color = C.muted; }}
                    >{t('users')}</button>
                </div>

                {/* Page header */}
                <div style={{ padding: isMobile ? '20px 16px 0' : '24px 28px 0 28px', flexShrink: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                        <div>
                            <div style={{ fontSize: 22, fontWeight: 800, color: C.text }}>{t('roles_and_permissions')}</div>
                            <div style={{ fontSize: 13, color: C.muted, marginTop: 2 }}>
                                {activeTab === 'roles' ? t('manage_roles_description') : t('manage_users_description')}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Loading state */}
                {loading ? (
                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.muted }}>
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: 14, fontWeight: 600 }}>{t('loading')}</div>
                        </div>
                    </div>
                ) : (
                    <>
                        {/* ═══════ ROLES TAB ═══════ */}
                        {activeTab === 'roles' && (
                            <div style={{ flex: 1, display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 16 : 24, padding: isMobile ? '0 16px 24px' : '0 28px 28px', overflow: 'hidden' }}>
                                {/* Left: Role list — on mobile, hidden once a role is selected (master→detail) */}
                                {!(isMobile && selectedRole) && (
                                <div style={{ width: isMobile ? '100%' : 340, flex: isMobile ? 1 : undefined, minHeight: isMobile ? 0 : undefined, flexShrink: 0, background: C.white, border: `1px solid ${C.border}`, borderRadius: 12, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                                    <div style={{ padding: 16, borderBottom: `1px solid ${C.border}` }}>
                                        <input
                                            style={{ width: '100%', padding: '8px 12px', border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 13, fontFamily: 'inherit', outline: 'none', color: C.text, marginBottom: 12, boxSizing: 'border-box' }}
                                            placeholder={t('search_roles')}
                                            value={roleSearch}
                                            onChange={e => setRoleSearch(e.target.value)}
                                            onFocus={e => { e.currentTarget.style.borderColor = C.green; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(37, 99, 235,0.1)'; }}
                                            onBlur={e => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.boxShadow = 'none'; }}
                                        />
                                        <button onClick={() => { setEditRoleTarget(null); setShowRoleForm(true); }} style={{
                                            width: '100%', background: C.green, color: C.white, border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600,
                                            cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, transition: 'background 0.15s',
                                        }}
                                            onMouseEnter={e => e.currentTarget.style.background = C.greenDark}
                                            onMouseLeave={e => e.currentTarget.style.background = C.green}
                                        >
                                            <span style={{ fontSize: 15 }}>+</span> {t('add_role')}
                                        </button>
                                    </div>
                                    <div style={{ flex: 1, overflowY: 'auto', padding: 8 }}>
                                        {filteredRoles.length === 0 ? (
                                            <RolesEmptyState filtered={!!roleSearch} t={t} />
                                        ) : (
                                            filteredRoles.map(r => {
                                                const rc = getRoleColor(r.name);
                                                const isSelected = selectedRole?.id === r.id;
                                                return (
                                                    <div key={r.id}
                                                        onClick={() => setSelectedRole(r)}
                                                        onContextMenu={e => {
                                                            e.preventDefault();
                                                            if (!r.is_default) {
                                                                setEditRoleTarget(r);
                                                                setShowRoleForm(true);
                                                            }
                                                        }}
                                                        style={{
                                                            padding: '14px 16px', borderRadius: 10, cursor: 'pointer',
                                                            border: `2px solid ${isSelected ? C.green : 'transparent'}`,
                                                            background: isSelected ? C.greenLight : 'transparent',
                                                            marginBottom: 6, transition: 'all 0.15s',
                                                        }}
                                                        onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = C.bg; }}
                                                        onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
                                                    >
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                                                            <div style={{ width: 8, height: 8, borderRadius: '50%', background: rc.dot, flexShrink: 0 }} />
                                                            <span style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{r.name}</span>
                                                            {r.is_default && (
                                                                <span style={{ fontSize: 10, fontWeight: 600, borderRadius: 20, padding: '2px 8px', background: '#f1f5f9', color: C.mutedLight, border: `1px solid ${C.border}` }}>
                                                                    {t('default')}
                                                                </span>
                                                            )}
                                                            <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 600, color: C.muted, background: '#f1f5f9', padding: '2px 8px', borderRadius: 12 }}>
                                                                {r.user_count} {r.user_count !== 1 ? t('users') : t('user')}
                                                            </span>
                                                        </div>
                                                        <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.4 }}>{r.description}</div>
                                                        {/* Edit/Delete buttons for non-default */}
                                                        {isSelected && (
                                                            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                                                                <button
                                                                    onClick={e => { e.stopPropagation(); setEditRoleTarget(r); setShowRoleForm(true); }}
                                                                    style={{ fontSize: 11, fontWeight: 600, color: C.green, background: 'none', border: 'none', cursor: 'pointer', padding: '2px 0', fontFamily: 'inherit' }}
                                                                >
                                                                    {t('edit')}
                                                                </button>
                                                                {!r.is_default && (
                                                                    <button
                                                                        onClick={e => { e.stopPropagation(); setDeleteRoleTarget(r); }}
                                                                        style={{ fontSize: 11, fontWeight: 600, color: C.red, background: 'none', border: 'none', cursor: 'pointer', padding: '2px 0', fontFamily: 'inherit' }}
                                                                    >
                                                                        {t('delete')}
                                                                    </button>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })
                                        )}
                                    </div>
                                </div>
                                )}

                                {/* Mobile back button (master→detail) */}
                                {isMobile && selectedRole && (
                                    <button onClick={() => setSelectedRole(null)} style={{ alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: C.green, fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit', padding: '4px 0', flexShrink: 0 }}>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
                                        {t('back_to_roles') || 'Back to roles'}
                                    </button>
                                )}

                                {/* Right: Permission editor — hidden on mobile until a role is picked */}
                                {(!isMobile || selectedRole) && (
                                <PermissionEditor
                                    role={selectedRole}
                                    permissions={rolePerms}
                                    onSave={handleSavePermissions}
                                    saving={saving}
                                    t={t}
                                />
                                )}
                            </div>
                        )}

                        {/* ═══════ USERS TAB ═══════ */}
                        {activeTab === 'users' && (
                            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: isMobile ? '16px 16px 24px' : '0 28px 28px', overflow: isMobile ? 'auto' : 'hidden' }}>
                                {/* Toolbar */}
                                <div style={{ display: 'flex', alignItems: 'center', flexWrap: isMobile ? 'wrap' : 'nowrap', gap: 12, marginBottom: 20, flexShrink: 0 }}>
                                    <input
                                        style={{ width: 260, padding: '8px 12px', border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 13, fontFamily: 'inherit', outline: 'none', color: C.text, boxSizing: 'border-box' }}
                                        placeholder={t('search_users')}
                                        value={userSearch}
                                        onChange={e => setUserSearch(e.target.value)}
                                        onFocus={e => { e.currentTarget.style.borderColor = C.green; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(37, 99, 235,0.1)'; }}
                                        onBlur={e => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.boxShadow = 'none'; }}
                                    />
                                    <select style={{ padding: '8px 12px', border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 13, fontFamily: 'inherit', color: C.text, background: C.white, cursor: 'pointer', outline: 'none' }}
                                        value={filterRole} onChange={e => setFilterRole(e.target.value)}>
                                        <option value="all">{t('all_roles')}</option>
                                        {roles.map(r => <option key={r.id} value={r.name}>{r.name}</option>)}
                                    </select>
                                    <select style={{ padding: '8px 12px', border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 13, fontFamily: 'inherit', color: C.text, background: C.white, cursor: 'pointer', outline: 'none' }}
                                        value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
                                        <option value="all">{t('all_status')}</option>
                                        <option value="active">{t('active')}</option>
                                        <option value="inactive">{t('inactive')}</option>
                                    </select>
                                    <div style={{ flex: 1 }} />
                                    <button onClick={() => { setEditUserTarget(null); setShowUserForm(true); }} style={{
                                        background: C.green, color: C.white, border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600,
                                        cursor: 'pointer', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 6, transition: 'background 0.15s',
                                    }}
                                        onMouseEnter={e => e.currentTarget.style.background = C.greenDark}
                                        onMouseLeave={e => e.currentTarget.style.background = C.green}
                                    >
                                        <span style={{ fontSize: 15 }}>+</span> {t('add_user')}
                                    </button>
                                </div>

                                {/* Mobile: hero + user cards */}
                                {isMobile ? (
                                    filteredUsers.length === 0 ? (
                                        <UsersEmptyState filtered={!!(userSearch || filterRole !== 'all' || filterStatus !== 'all')} onAdd={() => { setEditUserTarget(null); setShowUserForm(true); }} t={t} />
                                    ) : (
                                        <>
                                            <MobileHero
                                                label={t('users')}
                                                value={String(filteredUsers.length)}
                                                meta={`${roles.length} ${roles.length !== 1 ? t('roles') : t('role')}`}
                                            />
                                            {filteredUsers.map(u => (
                                                <MobileUserCard
                                                    key={u.id}
                                                    user={u}
                                                    roleName={getUserRoleName(u)}
                                                    permissions={u.role_id ? allPermissions[u.role_id] : null}
                                                    onEdit={(user) => { setEditUserTarget({ ...user, _roleName: getUserRoleName(user) }); setShowUserForm(true); }}
                                                    onResetPw={(user) => setResetPwTarget(user)}
                                                    onToggleActive={(user) => setDeactivateTarget(user)}
                                                    t={t}
                                                />
                                            ))}
                                        </>
                                    )
                                ) : (
                                /* Table + detail panel */
                                <div style={{ flex: 1, display: 'flex', gap: 0, overflow: 'hidden' }}>
                                    <div style={{ flex: 1, background: C.white, border: `1px solid ${C.border}`, borderRadius: 12, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                                        {filteredUsers.length === 0 ? (
                                            <UsersEmptyState filtered={!!(userSearch || filterRole !== 'all' || filterStatus !== 'all')} onAdd={() => { setEditUserTarget(null); setShowUserForm(true); }} t={t} />
                                        ) : (
                                            <div style={{ flex: 1, overflowY: 'auto' }}>
                                                <div className="portal-table-wrap">
                                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                                    <thead>
                                                        <tr>
                                                            {[t('user'), t('role'), t('status'), t('created'), t('actions')].map(h => (
                                                                <th key={h} style={{
                                                                    textAlign: 'left', padding: '12px 16px', fontSize: 11, fontWeight: 700,
                                                                    textTransform: 'uppercase', letterSpacing: 0.8, color: C.muted,
                                                                    background: C.bg, borderBottom: `1px solid ${C.border}`,
                                                                    position: 'sticky', top: 0, zIndex: 1,
                                                                }}>{h}</th>
                                                            ))}
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {filteredUsers.map(u => {
                                                            const isSelected = selectedUser?.id === u.id;
                                                            const displayName = u.display_name || u.username;
                                                            return (
                                                                <tr key={u.id}
                                                                    onClick={() => setSelectedUser(u)}
                                                                    style={{ cursor: 'pointer' }}
                                                                >
                                                                    <td style={{ padding: '14px 16px', fontSize: 13, color: C.text, borderBottom: '1px solid #f1f5f9', verticalAlign: 'middle', background: isSelected ? C.greenLight : undefined }}>
                                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                                                            <Avatar name={displayName} size={36} />
                                                                            <div>
                                                                                <div style={{ fontWeight: 600 }}>{displayName}</div>
                                                                                <div style={{ fontSize: 12, color: C.muted }}>{u.username}</div>
                                                                            </div>
                                                                        </div>
                                                                    </td>
                                                                    <td style={{ padding: '14px 16px', borderBottom: '1px solid #f1f5f9', verticalAlign: 'middle', background: isSelected ? C.greenLight : undefined }}>
                                                                        <RoleBadge role={getUserRoleName(u)} />
                                                                    </td>
                                                                    <td style={{ padding: '14px 16px', borderBottom: '1px solid #f1f5f9', verticalAlign: 'middle', background: isSelected ? C.greenLight : undefined }}>
                                                                        <StatusBadge active={u.is_active} t={t} />
                                                                    </td>
                                                                    <td style={{ padding: '14px 16px', borderBottom: '1px solid #f1f5f9', verticalAlign: 'middle', color: C.muted, fontSize: 12, background: isSelected ? C.greenLight : undefined }}>
                                                                        {formatDate(u.created_at)}
                                                                    </td>
                                                                    <td style={{ padding: '14px 16px', borderBottom: '1px solid #f1f5f9', verticalAlign: 'middle', background: isSelected ? C.greenLight : undefined }}>
                                                                        <div style={{ display: 'flex', gap: 4 }}>
                                                                            <button onClick={e => { e.stopPropagation(); setEditUserTarget({ ...u, _roleName: getUserRoleName(u) }); setShowUserForm(true); }}
                                                                                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px', borderRadius: 4, fontSize: 12, color: C.muted, fontFamily: 'inherit', fontWeight: 500, transition: 'all 0.15s' }}
                                                                                onMouseEnter={e => { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.color = C.text; }}
                                                                                onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = C.muted; }}
                                                                            >{t('edit')}</button>
                                                                            <button onClick={e => { e.stopPropagation(); setResetPwTarget(u); }}
                                                                                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px', borderRadius: 4, fontSize: 12, color: C.muted, fontFamily: 'inherit', fontWeight: 500, transition: 'all 0.15s' }}
                                                                                onMouseEnter={e => { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.color = C.text; }}
                                                                                onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = C.muted; }}
                                                                            >{t('reset_pw')}</button>
                                                                            <button onClick={e => { e.stopPropagation(); setDeactivateTarget(u); }}
                                                                                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px', borderRadius: 4, fontSize: 12, color: u.is_active ? C.muted : C.green, fontFamily: 'inherit', fontWeight: 500, transition: 'all 0.15s' }}
                                                                                onMouseEnter={e => { e.currentTarget.style.background = u.is_active ? C.redLight : C.greenLight; e.currentTarget.style.color = u.is_active ? C.red : C.green; }}
                                                                                onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = u.is_active ? C.muted : C.green; }}
                                                                            >{u.is_active ? t('deactivate') : t('reactivate')}</button>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Detail panel */}
                                    {selectedUser && (
                                        <UserDetailPanel
                                            user={selectedUser}
                                            roleName={getUserRoleName(selectedUser)}
                                            onClose={() => setSelectedUser(null)}
                                            onEdit={() => { setEditUserTarget({ ...selectedUser, _roleName: getUserRoleName(selectedUser) }); setShowUserForm(true); }}
                                            onResetPw={() => setResetPwTarget(selectedUser)}
                                            onToggleActive={() => setDeactivateTarget(selectedUser)}
                                            t={t}
                                        />
                                    )}
                                </div>
                                )}
                            </div>
                        )}
                    </>
                )}

                {/* ═══════ MODALS ═══════ */}
                {showRoleForm && (
                    <RoleFormModal
                        role={editRoleTarget}
                        onSave={handleSaveRole}
                        onClose={() => { setShowRoleForm(false); setEditRoleTarget(null); }}
                        saving={saving}
                        t={t}
                    />
                )}

                {showUserForm && (
                    <UserFormModal
                        user={editUserTarget}
                        roles={roles}
                        allPermissions={allPermissions}
                        onSave={handleSaveUser}
                        onClose={() => { setShowUserForm(false); setEditUserTarget(null); }}
                        saving={saving}
                        t={t}
                    />
                )}

                {resetPwTarget && (
                    <ResetPasswordModal
                        user={resetPwTarget}
                        onConfirm={handleResetPassword}
                        onClose={() => setResetPwTarget(null)}
                        saving={saving}
                        t={t}
                    />
                )}

                {deactivateTarget && (
                    <DeactivateModal
                        user={deactivateTarget}
                        onConfirm={handleToggleActive}
                        onClose={() => setDeactivateTarget(null)}
                        saving={saving}
                        t={t}
                    />
                )}

                {deleteRoleTarget && (
                    <DeleteRoleModal
                        role={deleteRoleTarget}
                        onConfirm={handleDeleteRole}
                        onClose={() => setDeleteRoleTarget(null)}
                        saving={saving}
                        t={t}
                    />
                )}
            </div>
        </PortalLayout>
    );
};

export default PortalRolesPage;
